/**
 * Base Axios Client
 * Centralized axios instance with interceptors for error handling and token management
 */
import axios from 'axios';
import { publishCreditsFromApiPayload } from '../../config/app/signupCredits';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8001";

/** Single source for API origin (used by axios + fetch-based pixel loaders). */
export const getApiBaseUrl = () => API_BASE_URL;

// When REACT_APP_ENV=development, use mock API responses so UI works without backend.
// Set REACT_APP_USE_MOCK_API=false in .env to hit real API in development.
export const isMockApi =
  process.env.REACT_APP_ENV === "development" &&
  process.env.REACT_APP_USE_MOCK_API !== "false";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds default timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    // Let the browser set multipart boundary for FormData uploads
    if (config.data instanceof FormData && config.headers) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — update left-nav credits when tools return remaining_credits
apiClient.interceptors.response.use(
  (response) => {
    try {
      publishCreditsFromApiPayload(response?.data);
    } catch {
      // never block the caller
    }
    return response;
  },
  (error) => {
    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        error.message = 'Request timeout - server may be offline';
      } else if (error.message.includes('Network Error') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        error.message = 'Cannot connect to server - please check your connection';
      }
    }
    
    // Handle HTTP errors
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          error.message = data?.detail || 'Authentication failed';
          break;
        case 403:
          error.message = data?.detail || 'Access forbidden';
          break;
        case 404:
          error.message = data?.detail || 'Resource not found';
          break;
        case 500:
          error.message = data?.detail || 'Server error';
          break;
        default:
          error.message = data?.detail || `Request failed with status ${status}`;
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Helper function to create authenticated request config
 * @param {string} token - Clerk session token
 * @param {Object} additionalHeaders - Additional headers to add
 * @returns {Object} Axios request config
 */
export const createAuthConfig = (token, additionalHeaders = {}) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  },
});

/** Publish remaining_credits (if present) then return data — for mock / non-axios paths. */
export function tapCredits(data) {
  try {
    publishCreditsFromApiPayload(data);
  } catch {
    // ignore
  }
  return data;
}

export default apiClient;





